import axios from "axios";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { API } from "../../../../config";
import { RootState } from "../../../../configureStore";
import {
  NewUserRow,
  SubmitResult,
} from "../../../slices/users/managerCreation";
import { generatePassword } from "../../locations/locationsAsyncActions";

// Template ID that identifies the backoffice manager role — its group is auto-assigned, not user-selected
const BACKOFFICE_MANAGER_TEMPLATE_ID = 28;

const registerUser = (token: string, locale: string, row: NewUserRow) =>
  axios.post(
    `${API}/users/register`,
    {
      username: row.email,
      email: row.email,
      password: generatePassword(),
      userType: "real",
      remoteId: "",
      firstname: "",
      lastname: "",
      associatedGeoPointId: row.siteId !== null ? Number(row.siteId) : null,
    },
    {
      headers: {
        "Accept-Language": locale,
        Authorization: `Bearer ${token}`,
      },
    },
  );

// Returns the groupTemplateIds already assigned to the user so we can diff against required ones
const fetchExistingGroupTemplateIds = async (
  token: string,
  locale: string,
  userId: string,
): Promise<number[]> => {
  const res = await axios.get(
    `${API}/users/organization-group-membership?$eager=[group]&userId=${userId}&deletedAt=null`,
    {
      headers: {
        "Accept-Language": locale,
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return (res.data.data as any[])
    .filter((m) => m.group?.groupTemplateId !== null)
    .map((m) => m.group.groupTemplateId);
};

const fetchGroupsWithTemplates = (
  token: string,
  locale: string,
  orgId: number,
  templateIds: number[],
) => {
  const qs = templateIds.map((id) => `groupTemplateId[$in][]=${id}`).join("&");
  return axios.get(
    `${API}/users/organization-group?$eager=[groupTemplate]&orgId=${orgId}&${qs}&deletedAt=null`,
    {
      headers: {
        "Accept-Language": locale,
        Authorization: `Bearer ${token}`,
      },
    },
  );
};

export const createManagerRole = (
  token: string,
  locale: string,
  userId: string,
  groupId: number,
  membershipType: number,
) =>
  axios.post(
    `${API}/users/organization-group-membership`,
    { userId, groupId, membershipType },
    {
      headers: {
        "Accept-Language": locale,
        Authorization: `Bearer ${token}`,
      },
    },
  );

export const createManagerUsers = createAsyncThunk(
  "managerCreation/createManagerUsers",
  async (_: void, store) => {
    const { rows, roles } = (store.getState() as RootState)
      .ManagerCreationReducer;
    const { orgId, user } = (store.getState() as RootState).Authentication;
    const { locale } = (store.getState() as RootState).LayoutReducer;
    const token = user?.token ?? "";

    const validRows = rows.filter((r) => r.status === "valid");
    const submitResults: SubmitResult[] = [];

    // Step 1: Create all users in parallel — allSettled so one failure doesn't cancel the rest
    const creationResults = await Promise.allSettled(
      validRows.map((row) => registerUser(token, locale, row)),
    );

    // Classify registration results
    type SuccessPair = { result: PromiseFulfilledResult<any>; row: NewUserRow };
    const createdPairs: SuccessPair[] = [];

    creationResults.forEach((result, i) => {
      const row = validRows[i];
      if (result.status === "rejected") {
        submitResults.push({
          rowId: row.id,
          email: row.email,
          success: false,
          errorMessage:
            result.reason?.response?.data?.message ?? "User creation failed",
        });
      } else {
        createdPairs.push({
          result: result as PromiseFulfilledResult<any>,
          row,
        });
      }
    });

    // Step 2: Assign group memberships — catch per-user so one failure doesn't block others
    await Promise.allSettled(
      createdPairs.map(async ({ result, row }) => {
        const userId: string = result.value.data.id;
        try {
          const roleOption = roles.find((r) => r.id === row.role);

          if (roleOption && roleOption.groupTemplateIds.length > 0) {
            // Diff against what the backend may have auto-assigned during registration
            const existingTemplateIds = await fetchExistingGroupTemplateIds(
              token,
              locale,
              userId,
            );
            const missingTemplateIds = roleOption.groupTemplateIds.filter(
              (id) => !existingTemplateIds.includes(id),
            );

            if (missingTemplateIds.length > 0) {
              if (missingTemplateIds.includes(BACKOFFICE_MANAGER_TEMPLATE_ID)) {
                // Backoffice manager: the group is auto-detected, no user selection needed
                const groupsRes = await fetchGroupsWithTemplates(
                  token,
                  locale,
                  orgId as number,
                  [BACKOFFICE_MANAGER_TEMPLATE_ID],
                );
                const backofficeGroup = groupsRes.data.data[0];
                if (backofficeGroup) {
                  await createManagerRole(
                    token,
                    locale,
                    userId,
                    backofficeGroup.id,
                    backofficeGroup.groupTemplate.defaultMembershipType,
                  );
                }
              } else {
                // Other manager types: use explicitly selected groups or fall back to all role groups
                const templateIdsToFetch =
                  row.groupsSelected.length > 0
                    ? [
                        ...new Set(
                          row.groupsSelected.map((g) => g.groupTemplateId),
                        ),
                      ]
                    : missingTemplateIds;

                const groupsRes = await fetchGroupsWithTemplates(
                  token,
                  locale,
                  orgId as number,
                  templateIdsToFetch,
                );
                const fetchedGroups: any[] = groupsRes.data.data;

                const groupsToAssign =
                  row.groupsSelected.length > 0
                    ? fetchedGroups.filter((g) =>
                        row.groupsSelected.some((sg) => sg.id === g.id),
                      )
                    : fetchedGroups;

                await Promise.all(
                  groupsToAssign.map((g) =>
                    createManagerRole(
                      token,
                      locale,
                      userId,
                      g.id,
                      g.groupTemplate.defaultMembershipType,
                    ),
                  ),
                );
              }
            }
          }

          submitResults.push({
            rowId: row.id,
            email: row.email,
            success: true,
          });
        } catch (err: any) {
          submitResults.push({
            rowId: row.id,
            email: row.email,
            success: false,
            errorMessage:
              err?.response?.data?.message ?? "Role assignment failed",
          });
        }
      }),
    );

    return submitResults;
  },
);
